import * as React from 'react';
import * as _ from 'underscore';
import { TabStyles } from '@tableau/widgets';
import { ButtonType, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { Messages } from '~src/codegen/Localize';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { WorkbookErrors } from '~src/content/services/WorkbookErrors';
import { Workbooks } from '~src/content/resources/Workbooks';
import { IWorkbookBase } from '~src/ts/models/IWorkbookInfo';
import { Set } from '~src/ts/models/Set';
import { L10n } from '~src/ts/L10n/L10n';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg, StandardActionToastMessages } from '~src/commonInfra/contentActions/ActionHelpers';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { ButtonWithSpinner } from '~src/common/react/components/ButtonWithSpinner';
import { ActionButtons, ActionButtonLeftWide } from '~src/commonInfra/dialogs/ReactActionDialog';
import { dialogBodyStyle } from '~src/app/content/styles/ExternalContentActionsStyles';

export namespace SetDisplayTabsAction {

  export interface SetDisplayTabsFooterProps {
    closeDialog: () => void;
    dismissDialog: () => void;
    showAllTabs: () => void;
    hideAllTabs: () => void;
    hideTabsDisabled: boolean;
    showTabsDisabled: boolean;
  }

  export const SetDisplayTabsFooter = (props: SetDisplayTabsFooterProps): JSX.Element => {
    const [showTabsBusy, setShowTabsBusy] = React.useState<boolean>(false);
    const [hideTabsBusy, setHideTabsBusy] = React.useState<boolean>(false);

    const showAllTabs = React.useCallback(async () => {
      if (props.showTabsDisabled) {
        return;
      }
      setShowTabsBusy(true);
      await props.showAllTabs();
      props.closeDialog();

    }, [setShowTabsBusy, props.showAllTabs, props.showTabsDisabled, props.closeDialog]);

    const hideAllTabs = React.useCallback(async () => {
      if (props.hideTabsDisabled) {
        return;
      }
      setHideTabsBusy(true);
      await props.hideAllTabs();
      props.closeDialog();

    }, [setHideTabsBusy, props.hideAllTabs, props.hideTabsDisabled, props.closeDialog]);

    return <div className={ActionButtons} >
      <ButtonWithSpinner
        buttonType={ButtonType.Outline}
        clicked={() => props.dismissDialog()}
        testId={'set-tab-cancel'}
        title={Messages.cancel()}
        disabled={showTabsBusy || hideTabsBusy}
      />
      <span className={ActionButtonLeftWide}>
        <ButtonWithSpinner
          buttonType={ButtonType.Primary}
          clicked={showAllTabs}
          testId={'confirm-show-tabs'}
          title={Messages.confirmSetDisplayTabsShowButton()}
          disabled={showTabsBusy || hideTabsBusy || props.showTabsDisabled}
          showSpinner={showTabsBusy}
        />
      </span>
      <span className={ActionButtonLeftWide}>
        <ButtonWithSpinner
          buttonType={ButtonType.Primary}
          clicked={hideAllTabs}
          testId={'confirm-hide-tabs'}
          title={Messages.confirmSetDisplayTabsHideButton()}
          disabled={showTabsBusy || hideTabsBusy || props.hideTabsDisabled}
          showSpinner={hideTabsBusy}
        />
      </span>
    </div>;
  };

  export function confirmSetDisplayTabs(workbooks: Set<IWorkbookBase>) {

    const workbookArray = workbooks.toArray();
    const anyTabsEnabled = _.any(workbookArray, (wb: IWorkbookBase) => wb.displayTabs);
    const anyTabsDisabled = _.any(workbookArray, (wb: IWorkbookBase) => !wb.displayTabs);

    const message = Messages.confirmSetDisplayTabsPrompt({
      ITEM_COUNT: workbooks.size(),
      ITEM_NAME: L10n.elideIfLong(workbooks.first().name),
      ITEM_COUNT_LOC: L10n.Interpolations.Count
    });

    const modal = ReactModalService.open({
      title:  Messages.confirmSetDisplayTabsTitle(),
      type: DialogType.NormalWithTitleBar,
      showCloseButton: false,
      dismissDialogOnCloseAction: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogContent: <>{message}</>,
      dialogBodyStyle: dialogBodyStyle,
      dialogWidth: TabDialogWidth.width500px,
      footerContent: <SetDisplayTabsFooter
        dismissDialog={() => modal.dismiss()}
        closeDialog={() => modal.close()}
        showAllTabs={() => setDisplayTabs(workbooks, true)}
        hideAllTabs={() => setDisplayTabs(workbooks, false)}
        showTabsDisabled={!anyTabsDisabled}
        hideTabsDisabled={!anyTabsEnabled} />
    });
    return Promise.resolve(modal.result);
  }

  function setDisplayTabs(workbooks: Set<IWorkbookBase>, displayTabs: boolean) {
    return Workbooks.setDisplayTabs(workbooks.keys(), displayTabs)
      .then((result: IResult) => {
        notifyResult(displayTabs, workbooks, result);
      });
  }

  function notifyResult(displayTabs: boolean, workbooks: Set<IWorkbookBase>, result: IResult) {

    const translations: StandardActionToastMessages = displayTabs ?
      {
        success: standardActionSuccessMsg(Messages.setDisplayTabsShowSuccess),
        error: standardActionErrorMsg(Messages.setDisplayTabsShowError),
      } : {
        success: standardActionSuccessMsg(Messages.setDisplayTabsHideSuccess),
        error: standardActionErrorMsg(Messages.setDisplayTabsHideError),
      };

    ActionHelpers.displayStandardToastsForResult(
      workbooks,
      result,
      translations,
      (errorItem: IErrorItem<IWorkbookBase>) => WorkbookErrors.errorDetailsFor(errorItem)
    );
  }
}

export type SetDisplayTabsAction = typeof SetDisplayTabsAction;